/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { ofType } from 'redux-observable';
import { combineLatest, of } from 'rxjs';
import { withLatestFrom, pluck, mergeMap } from 'rxjs/operators';
import toNumber from 'lodash/toNumber';
import includes from 'lodash/includes';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import lodashGet from 'lodash/get';
import { isSameDay } from '@internationalized/date';
import { localToday } from 'sarsaparilla';

import {
    ROLES_WITHOUT_FCFS_PERMISSION,
    ROLES_WITH_FCFS_PERMISSION_FOR_PREVIOUS_DAY,
} from 'ui-internal-camping/src/constants';
import {
    updateAvailabilitiesByUpdatedSelection,
    updateDataSource,
    updateStandardSelections,
    updateItinerarySelections,
    isInFlightModificationEnabled,
    completeDateRangeMap,
} from '../../utils/availabilityGridUtil';
import { ITINERARY_SINGLE_TYPE_FACILITY } from '../../constants/campground';
import {
    PROCESS_AVAILABILITY_COMPLETE,
    SELECT_LTS_CAMPSITE,
    UPDATE_AVAILABILITIES_COMPLETE,
    UPDATE_SELECTIONS,
    UPDATE_SINGLE_CAMPSITE_AVAILABILITIES_COMPLETE,
} from '../../constants';

import { UTCStringToCalendarDate } from '../../utils/dateUtils';

const isLTSCampsite = (campsites, campsiteId: string) => {
    return (
        lodashGet(
            campsites,
            `[${campsiteId}].campsite_rules.allowRecurringPayment.value`,
            0
        ) === 1
    );
};

export const updateAvailabilityEpic = (action$, state$) =>
    action$.pipe(
        ofType(
            UPDATE_SELECTIONS, // when a single campsite selection is updated
            PROCESS_AVAILABILITY_COMPLETE
        ),
        withLatestFrom(
            combineLatest([
                state$.pipe(pluck('availabilityGrid')),
                state$.pipe(pluck('campground'), pluck('campground'), pluck('results')),
                state$.pipe(
                    pluck('availableCampsites'),
                    pluck('results'),
                    pluck('campsites')
                ),
                state$.pipe(
                    pluck('selectedLocationRole'),
                    pluck('role'),
                    pluck('role_type')
                ),
                state$.pipe(
                    pluck('internalCamping'),
                    pluck('orderDetails'),
                    pluck('pendingReservations')
                ),
                state$.pipe(pluck('orderDetails'), pluck('pendingReservations')),
                state$.pipe(pluck('earlyAccessLottery')),
                state$.pipe(
                    pluck('internalCamping'),
                    pluck('orderDetails'),
                    pluck('reservations')
                ),
                state$.pipe(pluck('orderDetails'), pluck('reservations')),
            ])
        ),
        mergeMap((stream) => {
            const actionType = stream[0];
            const states = stream[1];
            const [
                availabilityData,
                campground,
                campsites,
                role,
                pendingReservations,
                externalPendingReservations,
                earlyAccessLottery,
                internalReservations,
                externalReservations,
            ] = states;
            const {
                originalAvailabilitiesMap,
                processedAvailabilitiesMap,
                selections,
                groupSize,
            } = availabilityData;
            const reservation =
                !isEmpty(pendingReservations) && values(pendingReservations)[0];
            const externalReservation =
                !isEmpty(externalPendingReservations) &&
                values(externalPendingReservations)[0];
            const externalOriginalReservation =
                !isEmpty(externalReservations) && values(externalReservations)[0];
            const internalOriginalReservation =
                !isEmpty(internalReservations) && values(internalReservations)[0];

            const {
                campsiteId,
                date,
                isInternal,
                isTransfer,
                name,
                loop,
                isLTSSelection,
                isBooking,
            } = actionType.payload;

            const canModifyInflightReservation =
                !isBooking &&
                isInFlightModificationEnabled(
                    campground,
                    externalOriginalReservation || internalOriginalReservation,
                    isInternal
                );
            const isModifyingReservation = !isEmpty(
                externalOriginalReservation || internalOriginalReservation
            );
            const isDayUseReservation =
                lodashGet(
                    externalOriginalReservation || internalOriginalReservation,
                    'extra_info.type_of_use'
                ) === 'Day';
            const canModifyAsItinerary = canModifyInflightReservation;
            const isItinerarySingle =
                campground.facility_type === ITINERARY_SINGLE_TYPE_FACILITY;
            const canBookFCFS =
                isInternal && !includes(ROLES_WITHOUT_FCFS_PERMISSION, role);

            const itineraryFlow =
                isItinerarySingle ||
                (isModifyingReservation && !isBooking && !isDayUseReservation);

            let newSelections = selections;

            if (actionType.type === UPDATE_SELECTIONS) {
                // if campsite is LTS open modal
                if (
                    isLTSCampsite(originalAvailabilitiesMap, campsiteId) &&
                    !isLTSSelection
                ) {
                    return of({
                        type: SELECT_LTS_CAMPSITE,
                        payload: {
                            campsiteId,
                            startDate: date,
                        },
                    });
                }

                const cDate = UTCStringToCalendarDate(date);
                const canBookFCFSYesterday =
                    date &&
                    isSameDay(cDate, localToday.subtract({ days: 1 })) &&
                    includes(ROLES_WITH_FCFS_PERMISSION_FOR_PREVIOUS_DAY, role);

                // 1) get updated selections object if new selection is valid/available
                // - if the facility is itinerary single
                // - if we are modifying a reservation
                newSelections = itineraryFlow
                    ? updateItinerarySelections({
                          // ITINERARY SINGLE
                          selections,
                          newDate: cDate,
                          newCampsiteId: campsiteId,
                          newCampsiteName: lodashGet(
                              originalAvailabilitiesMap,
                              `${campsiteId}.site`,
                              ''
                          ),
                          newCampsiteLoop: lodashGet(
                              originalAvailabilitiesMap,
                              `${campsiteId}.loop`,
                              ''
                          ),
                          originalReservation:
                              externalOriginalReservation || internalOriginalReservation,
                          availabilitiesData: processedAvailabilitiesMap[campsiteId],
                          itineraryFlow,
                          isInternal: canBookFCFS,
                          campgroundDateRange: processedAvailabilitiesMap,
                          canModifyAsItinerary,
                      })
                    : updateStandardSelections({
                          // STANDARD || ITINERARY
                          selections,
                          newDate: cDate,
                          newCampsiteId: campsiteId,
                          reservation,
                          externalReservation,
                          availabilitiesData: processedAvailabilitiesMap[campsiteId],
                          isInternal: canBookFCFS || canBookFCFSYesterday,
                          isTransfer,
                          canModifyInflightReservation,
                          name,
                          loop,
                      });
            }

            // === ITINERARY SINGLE OR if new availability data fetched ===
            /* 2) get new availabilities objects for ALL campsites */
            if (actionType.type === PROCESS_AVAILABILITY_COMPLETE || itineraryFlow) {
                if (itineraryFlow) {
                    newSelections = completeDateRangeMap(newSelections);
                }
                const availabilitiesMap = updateDataSource({
                    campground,
                    campsites,
                    selections: newSelections,
                    availabilitiesMap: originalAvailabilitiesMap,
                    groupSize: toNumber(groupSize),
                    newCampsiteId: campsiteId,
                    canBookFCFS,
                    isItinerarySingle,
                    itineraryFlow,
                    isModifyingReservation: isModifyingReservation && !isBooking,
                    earlyAccessLotteries: lodashGet(earlyAccessLottery, 'lotteries', {}),
                    internalOriginalReservation,
                });

                return of({
                    type: UPDATE_AVAILABILITIES_COMPLETE,
                    payload: {
                        newAvailabilities: availabilitiesMap,
                        newSelections,
                        shouldContinueLoading: false,
                    },
                });
            }
            // ==== STANDARD || ITINERARY ====
            /* 2) get new availabilities object for only the SINGLE updated campsite,
        so that we don't need to clone the entire availabilities
        map and cause a re-rendering of the whole grid */
            const availabilities = updateAvailabilitiesByUpdatedSelection({
                campground,
                updatedSelection: newSelections[campsiteId],
                campsite: originalAvailabilitiesMap[campsiteId],
                facilityType: campground.facility_type,
                canBookFCFS,
                earlyAccessLotteries: lodashGet(earlyAccessLottery, 'lotteries', {}),
                internalOriginalReservation,
            });
            return of({
                type: UPDATE_SINGLE_CAMPSITE_AVAILABILITIES_COMPLETE,
                payload: {
                    newCampsiteAvailabilities: availabilities,
                    campsiteId,
                    newSelections,
                    shouldContinueLoading: false,
                },
            });
        })
    );
